import React, { useRef, useState, useEffect } from "react";
import { SovosLinearProgress } from "mosaic-react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import styles from "colbi_web_ui/lib/components/DataGrid/DataGrid.module.sass";
import { getConfigVariable } from "../../config/config";
import SovosDialog from "mosaic-react/sovos-dialog";
import SovosDialogTitle from "mosaic-react/sovos-dialog-title";
import SovosDialogContent from "mosaic-react/sovos-dialog-content";
import SovosDialogActions from "mosaic-react/sovos-dialog-actions";
import SovosButton from "mosaic-react/sovos-button";
import { DataGrid, rows } from "colbi_web_ui/lib/components";
import { generateCsvWarningCard } from "./utils";

const Dropzone = ({ Children, childrenProps, translationId }) => {
    const apiDomain = getConfigVariable("REACT_APP_PROXY_HOST");
    const datagridRef = useRef(null);
    const [showDragOverlay, setShowDragOverlay] = useState(false);
    const [showLinearProgress, setShowLinearProgress] = useState(false);
    const [showErrorLines, setShowErrorLines] = useState(null);

    const { pushNotification, i18n, reload } = useGlobalValue();

    const dragChange = (e) => {
        e.stopImmediatePropagation();
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter") {
            setShowDragOverlay(true);
        } else if (e.type === "dragleave" && !e.currentTarget.contains(e.relatedTarget)) {
            setShowDragOverlay(false);
        } else if (e.type === "drop") {
            setShowLinearProgress(true);
            const files = (e.dataTransfer || { files: [] }).files;
            const fileList = [...files].filter((f) => f.type === "csv" || f.type === "text/csv");
            if (fileList.length !== files.length) {
                pushNotification({
                    message: i18n`${"Unable to upload file. Supported file: csv"}`,
                    type: "error",
                    open: true,
                    timeout: "medium",
                });
                setShowDragOverlay(false);
                return false;
            }
            setShowDragOverlay(false);

            const file = fileList[0];
            new Promise((resolve, reject) => {
                const request = new XMLHttpRequest();
                request.onreadystatechange = () => {
                    if (request.readyState === 4) {
                        try {
                            const { errorLines } = JSON.parse(request.response) || {};
                            setShowErrorLines(errorLines);
                            resolve(file?.name);
                        } catch (error) {
                            reject(error);
                        }
                    }
                };

                const data = new FormData();
                data.append("file", file);
                data.append("translationId", translationId);

                request.open("POST", apiDomain + "/api/upload_translation_table", true);
                if (apiDomain) {
                    request.withCredentials = true;
                }
                request.send(data);
            })
                .then((res) => {
                    if (!res?.errorLines) {
                        reload && reload();
                    }
                })
                .catch((e) => {
                    console.error(e);
                    pushNotification({
                        message: i18n`Somethings went wrong upload your data translation`,
                        type: "error",
                        open: true,
                        timeout: "medium",
                    });
                })
                .finally(() => {
                    setShowLinearProgress(false);
                });
        }
        return false;
    };

    useEffect(() => {
        const handleDocumentDragOver = (e) => e.preventDefault();
        const handleDocumentDrop = (e) => e.preventDefault();
        document.addEventListener("dragover", handleDocumentDragOver);
        document.addEventListener("drop", handleDocumentDrop);

        datagridRef.current?.addEventListener("dragover", dragChange);
        datagridRef.current?.addEventListener("dragenter", dragChange);
        datagridRef.current?.addEventListener("dragleave", dragChange);
        datagridRef.current?.addEventListener("dragexit", dragChange);
        datagridRef.current?.addEventListener("dragstart", dragChange);
        datagridRef.current?.addEventListener("drop", dragChange);

        return () => {
            document.removeEventListener("dragover", handleDocumentDragOver);
            document.removeEventListener("drop", handleDocumentDrop);

            datagridRef.current?.removeEventListener("dragover", dragChange);
            datagridRef.current?.removeEventListener("dragenter", dragChange);
            datagridRef.current?.removeEventListener("dragleave", dragChange);
            datagridRef.current?.removeEventListener("dragexit", dragChange);
            datagridRef.current?.removeEventListener("dragstart", dragChange);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            datagridRef.current?.removeEventListener("drop", dragChange);
        };
    });

    return (
        <div ref={datagridRef} className={`${styles["root"]}`}>
            {showLinearProgress && <SovosLinearProgress />}
            {showDragOverlay && (
                <div
                    className={`${styles["dropzone"]}`}
                    style={{
                        height: "100%",
                    }}
                >
                    <p style={{ color: "#072448" }}>Drop your files here</p>
                </div>
            )}
            <SovosDialog
                open={showErrorLines?.length || false}
                onClose={() => {
                    setShowErrorLines(null);
                    reload && reload();
                }}
            >
                <SovosDialogTitle>Csv Warnings</SovosDialogTitle>
                <SovosDialogContent>
                    <DataGrid
                        id="results"
                        card={rows.CellCard}
                        items={generateCsvWarningCard(showErrorLines)}
                        header={[
                            {
                                label: ["Csv line"],
                                type: "string",
                                columnName: "line",
                                align: "left",
                            },
                            {
                                label: ["Header Column"],
                                columnName: "headerColumn",
                                type: "string",
                                align: "left",
                            },
                            {
                                label: ["Invalid Column Name"],
                                columnName: "invalidColumnName",
                                type: "string",
                                align: "left",
                            },
                        ]}
                        pageSize={showErrorLines?.length}
                        metadata={{ total: showErrorLines?.length }}
                        status={{ loading: false }}
                    />
                </SovosDialogContent>
                <SovosDialogActions>
                    <SovosButton
                        variant="text"
                        onClick={() => {
                            setShowErrorLines(null);
                            reload && reload();
                        }}
                    >
                        Got it!
                    </SovosButton>
                </SovosDialogActions>
            </SovosDialog>
            <Children {...childrenProps} />
        </div>
    );
};

export default Dropzone;
